/**
 * PT-01 Em-dash density (INV-CH-PT-01; changes C-SPL, C-PT01-HYPHEN; ED-11 as amended by ED-17).
 *
 * Events per prose sentence (sentence boundaries from the new splitter, C-SPL): every em/en dash
 * counts 1; each pair of dashes in one sentence (the machine's appositive reflex) adds 2 more.
 * Spaced hyphens " - " are a different character (ED-15) and are evidence only, weight 0, whether
 * single or paired (ED-17; the seed weighted a pair 1.5). Exposure: prose words. Ramp: seed.
 */
import { fmt, line, proseBlocks, rateDef, sentenceRange, wordExposure } from './a-shared.ts';
import { makeEvidence, rateChannel } from './framework.ts';
import { proseSentenceList } from './helpers.ts';
import type { ChannelImpl, Measurement, RunContext } from './types.ts';

const W_PAIR = 2;
const EM = 0x2014;
const EN = 0x2013;
const SPACED_HYPHEN = /\s-\s/g;
/** Block pre-test: a dash, or a hyphen between whitespace. */
const QUICK = /[\u2013\u2014]|\s-\s/;

export const pt01Def = rateDef({
	id: 'PT-01',
	name: 'Em-dash density',
	family: 'PROSODY',
	features: [
		{ id: 'dash', label: 'Em/en dash' },
		{ id: 'dashPair', label: 'Appositive dash pair (extra weight)' },
		{ id: 'hyphenPair', label: 'Spaced-hyphen pair (evidence only)' },
		{ id: 'hyphenSingle', label: 'Single spaced hyphen (evidence only)' },
	],
	oneWay: true,
	minExposure: 250,
	exposureUnit: 'words',
	lang: 'agnostic',
	ramp: [
		[0, 8],
		[0.5, 22],
		[1.5, 50],
		[3, 76],
		[5, 90],
		[8, 97],
	],
	unitScale: 1000,
	unitLabel: 'weighted dashes per 1k words',
	phi0: 1.2,
	weight: 1.9,
	nd: 0.5,
	changes: ['C-SPL', 'C-PT01-HYPHEN'],
});

function measure(ctx: RunContext): Measurement {
	const { doc } = ctx;
	const text = doc.text;
	const k = new Float64Array(doc.paragraphs.count);
	const e = wordExposure(doc);
	const ev = makeEvidence();
	const sl = proseSentenceList(doc);
	let dashes = 0;
	let pairs = 0;
	let hyphens = 0;
	const dashPos: number[] = [];
	const hyPos: number[] = [];
	for (const b of proseBlocks(doc)) {
		if (!QUICK.test(b.text)) continue;
		for (let i = sl.paraFirst[b.p] as number; i < (sl.paraFirst[b.p + 1] as number); i++) {
			scanSentence(i);
		}
	}
	function scanSentence(i: number): void {
		const { start, end } = sentenceRange(doc, i);
		dashPos.length = 0;
		hyPos.length = 0;
		let hasHyphen = false;
		for (let c = start; c < end; c++) {
			const ch = text.charCodeAt(c);
			if (ch === EM || ch === EN) dashPos.push(c);
			else if (ch === 0x2d) hasHyphen = true;
		}
		if (hasHyphen) {
			const seg = text.slice(start, end);
			SPACED_HYPHEN.lastIndex = 0;
			for (let m = SPACED_HYPHEN.exec(seg); m !== null; m = SPACED_HYPHEN.exec(seg))
				hyPos.push(start + m.index + 1);
			SPACED_HYPHEN.lastIndex = 0;
		}
		if (dashPos.length === 0 && hyPos.length === 0) return;
		const p = sl.para[i] as number;
		let kk = 0;
		for (const d of dashPos) {
			ev.add(d, d + 1, 0, 1);
			kk += 1;
		}
		const dp = Math.floor(dashPos.length / 2);
		for (let j = 0; j < dp; j++) {
			ev.add(dashPos[2 * j] as number, (dashPos[2 * j + 1] as number) + 1, 1, W_PAIR);
			kk += W_PAIR;
		}
		const hp = Math.floor(hyPos.length / 2);
		for (let j = 0; j < hp; j++) {
			// ED-17: evidence only, weight 0 (a hyphen is not a dash, ED-15)
			ev.add(hyPos[2 * j] as number, (hyPos[2 * j + 1] as number) + 1, 2, 0);
		}
		if (hyPos.length % 2 === 1) {
			const h = hyPos[hyPos.length - 1] as number;
			ev.add(h, h + 1, 3, 0);
		}
		k[p] = (k[p] as number) + kk;
		dashes += dashPos.length;
		pairs += dp;
		hyphens += hyPos.length;
	}
	let words = 0;
	let K = 0;
	for (let p = 0; p < k.length; p++) {
		words += e[p] as number;
		K += k[p] as number;
	}
	const rate = (K / Math.max(1, words)) * 1000;
	const summary: string[] = [];
	if (dashes > 0)
		summary.push(
			line(
				`${dashes} dash${dashes === 1 ? '' : 'es'}${pairs ? ` incl. ${pairs} appositive pair${pairs === 1 ? '' : 's'}` : ''}`,
				`${fmt(rate, 2)} weighted per 1000 words`
			)
		);
	if (hyphens > 0)
		summary.push(
			line(
				`${hyphens}\u00d7 spaced hyphen " - "`,
				'not a dash (ED-15) \u00b7 evidence only, not in the score'
			)
		);
	return {
		k,
		e,
		evidence: ev,
		summary,
		features: { dashes, dashPairs: pairs, spacedHyphens: hyphens },
	};
}

export const pt01: ChannelImpl = rateChannel(pt01Def, measure);
export { pt01Def as def, pt01 as impl };
